import React, { useEffect, useState } from 'react';
import { Modal, Button, Table } from 'react-bootstrap';
import moment from 'moment';
import { ServiceData } from './Service';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apidefinitions from '../../../../core/api/apiDefinitions';
import ViewServiceModal from './ViewServiceModal';
import CompleteModule from './CompleteModule';

interface PendingCompletedModuleProps {
  show: boolean;
  onHide: () => void;
  statusColors: { [key: string]: string };
}

const PendingCompletedModule: React.FC<PendingCompletedModuleProps> = ({ show, onHide, statusColors }) => {
  const [services, setServices] = useState<ServiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [showCompleteModal, setShowCompleteModal] = useState<boolean>(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
  const [initialPartName, setInitialPartName] = useState<string | null>(null);

  useEffect(() => {
    if (show && checkPermission('ROLE_SERVICE')) {
      const fetchServices = async () => {
        setLoading(true);
        setError(null);
        try {
          const payload = {
            filterType: 'SERVICE_STATUS',
            filterValue: 'PENDING',
            date: '',
          };
          const response = await apidefinitions.getVehiclesServiceData(1, payload);
          const fetchedServices: ServiceData[] = response.data.data.vehiclesServiceData || [];
          setServices(fetchedServices);
        } catch (err) {
          setError('Failed to load pending completed services. Please try again.');
        } finally {
          setLoading(false);
        }
      };
      fetchServices();
    }
  }, [show]);

  const handleView = (service: ServiceData) => {
    const partName = service.servicedVehicleParts.length > 0 ? service.servicedVehicleParts[0].name : 'Unknown Part';
    setInitialPartName(partName);
    setSelectedService(service);
    setSelectedServiceId(service.id);
    setShowViewModal(true);
  };

  const handleComplete = (service: ServiceData) => {
    setSelectedService(service);
    setShowCompleteModal(true);
  };

  const handleCompleteSuccess = () => {
    // Refresh services after successful completion
    const fetchServices = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          filterType: 'SERVICE_STATUS',
          filterValue: 'PENDING',
          date: '',
        };
        const response = await apidefinitions.getVehiclesServiceData(1, payload);
        const fetchedServices: ServiceData[] = response.data.data.vehiclesServiceData || [];
        setServices(fetchedServices);
      } catch (err) {
        setError('Failed to load pending completed services. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  };

  return (
    <>
      <Modal show={show && checkPermission('ROLE_SERVICE')} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className="text-white">
          <Modal.Title>Pending Completed Services</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center">Loading...</div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Vehicle ID</th>
                    <th>Service Type</th>
                    <th>Service KMs</th>
                    <th>Service Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.length > 0 ? (
                    services.map((service) => (
                      <tr key={service.id}>
                        <td>{service.id}</td>
                        <td>{service.vehicleId}</td>
                        <td>{service.serviceType}</td>
                        <td>{service.serviceKms}</td>
                        <td>
                          <span className={statusColors[service.serviceStatus || ''] || 'badge bg-secondary'}>
                            {service.serviceStatus || 'N/A'}
                          </span>
                        </td>
                        <td>{moment(service.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                        <td>
                          <div className="hstack gap-2 fs-3">
                            <Button
                              variant="icon btn-secondary rounded-circle btn-sm"
                              onClick={() => handleView(service)}
                              title="View Service"
                            >
                              <i className="feather-eye" />
                            </Button>
                            {service.serviceStatus === 'PENDING' && (
                              <Button
                                variant="icon btn-success rounded-circle btn-sm"
                                onClick={() => handleComplete(service)}
                                title="Complete Service"
                              >
                                <i className="feather-check" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No pending completed services found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ViewServiceModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        serviceId={selectedServiceId}
        statusColors={statusColors}

      />
      <CompleteModule
        show={showCompleteModal}
        onHide={() => setShowCompleteModal(false)}
        serviceId={selectedService?.id || 0}
        onComplete={handleCompleteSuccess}
        initialPartName={initialPartName}
      />
    </>
  );
};

export default PendingCompletedModule;